import { useMemo, useState } from "react";
import { Pressable, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  BAND_GLYPH,
  bandLabel,
  buildPlanMatrix,
  formatSetGroup,
  muscleName,
  plannedTargetLabel,
  type Exercise,
  type Locale,
  type PlannedExerciseEntry,
  type UnitsPreference,
} from "@heraion/shared";

import type { UiTheme } from "../theme";
import { CellMarks } from "../IntensityGrid/CellMarks";
import { ScaleLegend } from "../primitives/ScaleLegend";
import { makePreviewStyles } from "../Programs/previewStyles";
import { weightUnitLabels } from "../weightUnitLabels";

// The plan's colpo d'occhio: an exercise × set heat matrix (each cell's
// colour is that set's band) so the workout's shape — where the heavy/top
// sets sit — reads at a glance, the program-grid idea applied to a single
// day. Plus an on-demand per-exercise detail (the full prescription line,
// muscles, notes), mirroring the program's tap-a-day → see-its-lifts.
// Every cell shows its value (program-grid parity, 2026-06-11); the
// selected row reads as a brand ring. A prescribed set with no banded
// intensity renders outlined, never as an empty cell.
export function PlanIntensityMatrix({
  entries,
  catalogBySlug,
  exerciseName,
  lang,
  unit,
  theme,
  styles,
}: {
  entries: PlannedExerciseEntry[];
  catalogBySlug: Map<string, Exercise>;
  exerciseName: (slug: string, snapshot?: string) => string;
  lang: Locale;
  // Display unit for prescribed absolute loads. Stored in kg; an lb reader
  // must see lb here like everywhere else. Required.
  unit: UnitsPreference;
  theme: UiTheme;
  styles: ReturnType<typeof makePreviewStyles>;
}) {
  const { t } = useTranslation();
  const matrix = useMemo(() => buildPlanMatrix(entries), [entries]);
  const [selected, setSelected] = useState(0);

  const selectedEntry = entries[selected];

  return (
    <View>
      <Text style={styles.sectionLabel}>
        {t("mobile.preview.matrixTitle", { defaultValue: "Intensity · by set" })}
      </Text>

      {matrix.setCols > 0 ? (
        <View style={styles.matrixHeadRow}>
          {Array.from({ length: matrix.setCols }, (_, i) => (
            <Text key={i} style={styles.matrixSetHead}>{`${i + 1}`}</Text>
          ))}
        </View>
      ) : null}

      {matrix.rows.map((row, idx) => {
        const isSel = idx === selected;
        return (
          <Pressable
            key={`${row.exerciseId}_${idx}`}
            accessibilityRole="button"
            accessibilityState={{ selected: isSel }}
            accessibilityLabel={`${exerciseName(row.exerciseId, row.nameSnapshot)}${
              row.band ? ` · ${bandLabel(t, row.band)}` : ""
            }`}
            onPress={() => setSelected(idx)}
            style={({ pressed }) => [
              styles.matrixExRow,
              isSel && styles.matrixExRowSelected,
              pressed && styles.matrixExRowPressed,
            ]}
          >
            <Text
              style={[styles.matrixExName, isSel && styles.matrixExNameSelected]}
              numberOfLines={1}
            >
              {exerciseName(row.exerciseId, row.nameSnapshot)}
            </Text>
            <View style={styles.matrixCells}>
              {row.cells.map((cell, c) => {
                const free = cell.prescribed && !cell.band;
                const colour = theme.intensity[cell.band ?? "none"];
                return (
                  <View
                    key={c}
                    style={[
                      styles.gridCell,
                      { backgroundColor: colour.fill },
                      free && styles.gridCellFree,
                    ]}
                  >
                    {/* Band glyph: the non-colour cue (W/M/H/X), a line above the
                        value so it never covers it. */}
                    {cell.band ? (
                      <CellMarks
                        theme={theme}
                        glyph={BAND_GLYPH[cell.band]}
                        ink={colour.on}
                        size="xs"
                        weight="700"
                      />
                    ) : null}
                    {cell.value ? (
                      <Text style={[styles.gridCellNum, { color: colour.on }]}>
                        {cell.value}
                      </Text>
                    ) : null}
                  </View>
                );
              })}
            </View>
          </Pressable>
        );
      })}

      <ScaleLegend
        variant="bands"
        theme={theme}
        includeFree
        style={styles.legendRow}
      />
      <Text style={styles.gridHint}>
        {t("mobile.preview.matrixHint", {
          defaultValue: "Tap an exercise to see its sets.",
        })}
      </Text>

      {selectedEntry ? (
        <SelectedExerciseDetail
          entry={selectedEntry}
          name={exerciseName(selectedEntry.exerciseId, selectedEntry.exerciseName)}
          muscles={(catalogBySlug.get(selectedEntry.exerciseId)?.primary ?? [])
            .map((m) => muscleName(m, lang))
            .join(", ")}
          unit={unit}
          lang={lang}
          styles={styles}
        />
      ) : null}
    </View>
  );
}

// One selected exercise's structured detail: name (+ AMRAP badge), muscles,
// the collapsed prescription line, and the authored note. Mirrors the
// program preview's SelectedDayDetail, applied to a single exercise.
function SelectedExerciseDetail({
  entry,
  name,
  muscles,
  unit,
  lang,
  styles,
}: {
  entry: PlannedExerciseEntry;
  name: string;
  muscles: string;
  unit: UnitsPreference;
  lang: Locale;
  styles: ReturnType<typeof makePreviewStyles>;
}) {
  const { t } = useTranslation();
  const unitLabels = weightUnitLabels(t);
  const hasAmrap = entry.sets.some((set) => set.amrap === true);
  const summary = formatSetGroup(
    entry.sets.map((set) => ({
      reps: set.targetReps,
      label: plannedTargetLabel(set, unit, lang, unitLabels),
      amrap: set.amrap === true,
      // #336 S3: «10 (8-12)», the editor's form.
      ...(set.repsMin !== undefined && set.repsMax !== undefined
        ? { range: { min: set.repsMin, max: set.repsMax } }
        : {}),
    })),
  );
  return (
    <View style={styles.slotCard}>
      <View style={styles.slotTitleRow}>
        <Text style={styles.slotName}>{name}</Text>
        {hasAmrap ? (
          <View style={styles.badge}>
            <Text style={styles.badgeText}>
              {t("program.amrapBadge", { defaultValue: "AMRAP" })}
            </Text>
          </View>
        ) : null}
      </View>
      {muscles ? <Text style={styles.slotMuscles}>{muscles}</Text> : null}
      <View style={styles.divider} />
      <View style={styles.weekRow}>
        <Text style={styles.weekSummary}>{summary}</Text>
      </View>
      {entry.notes ? <Text style={styles.slotNote}>{entry.notes}</Text> : null}
    </View>
  );
}
